import { autoUpdate, FloatingPortal, useFloating } from '@floating-ui/react'
import type { PrimitiveAtom } from 'jotai'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { coverReferenceMiddleware } from '../../../utilities/cover-reference-middleware'
import { layerAtomFamily, selectedLayerIdAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { FloatAnchor, LayerType } from '../data/v1'
import { BoxModelOverlay, useBoxModelFlash } from './box-model-overlay'
import { RESIZABLE_LAYER_TYPES } from './constants'
import { DragOverlay } from './drag-overlay'
import { ResizeHandles } from './resize-handles'
import { useLayerElement } from './use-layer-element'

const ANCHOR_POSITION: Record<FloatAnchor, { top: string; left: string }> = {
	'top-left': { top: '0%', left: '0%' },
	'top-center': { top: '0%', left: '50%' },
	'top-right': { top: '0%', left: '100%' },
	'center-left': { top: '50%', left: '0%' },
	'center': { top: '50%', left: '50%' },
	'center-right': { top: '50%', left: '100%' },
	'bottom-left': { top: '100%', left: '0%' },
	'bottom-center': { top: '100%', left: '50%' },
	'bottom-right': { top: '100%', left: '100%' },
}

function AnchorMarker({ anchor }: { anchor: FloatAnchor }) {
	const pos = ANCHOR_POSITION[anchor]
	return (
		<div
			style={{
				position: 'absolute',
				top: pos.top,
				left: pos.left,
				transform: 'translate(-50%, -50%)',
				pointerEvents: 'none',
			}}
		>
			{/* Crosshair */}
			<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
				<circle cx="7" cy="7" r="3" stroke="#3b82f6" strokeWidth="1.5" fill="white" fillOpacity="0.8" />
				<line x1="7" y1="0" x2="7" y2="4" stroke="#3b82f6" strokeWidth="1" />
				<line x1="7" y1="10" x2="7" y2="14" stroke="#3b82f6" strokeWidth="1" />
				<line x1="0" y1="7" x2="4" y2="7" stroke="#3b82f6" strokeWidth="1" />
				<line x1="10" y1="7" x2="14" y2="7" stroke="#3b82f6" strokeWidth="1" />
			</svg>
		</div>
	)
}

export function GizmoBox(
	{ layerIdAtom, border, typeFilter, showBoxModel, showResizeHandles, showDragOverlay, hideWhenSelected }: {
		layerIdAtom: PrimitiveAtom<string | null>
		border: string
		typeFilter?: Set<LayerType>
		showBoxModel?: boolean
		showResizeHandles?: boolean
		showDragOverlay?: boolean
		hideWhenSelected?: boolean
	},
) {
	const store = useCanvasStore()
	const layerId = useAtomValue(layerIdAtom, { store })
	const selectedId = useAtomValue(selectedLayerIdAtom, { store })
	const layer = useAtomValue(layerAtomFamily(layerId ?? ''), { store })
	const boxModelVisible = useBoxModelFlash(showBoxModel ? layer : null)

	const visible = layerId != null
		&& layer != null && !layer.hidden && !layer.locked
		&& (!typeFilter || typeFilter.has(layer.type))
		&& (!hideWhenSelected || layerId !== selectedId)

	// Re-query the DOM element when visibility or layer data changes.
	// `layer` as a dep catches hidden toggling (including parent-caused unmounts)
	// and the isConnected check in the hook catches any other detachment.
	const referenceEl = useLayerElement(visible ? layerId : null, [layer])

	const { refs, floatingStyles, update } = useFloating({
		middleware: coverReferenceMiddleware(),
		elements: { reference: referenceEl },
		whileElementsMounted: (...args) => autoUpdate(...args, { animationFrame: true }),
	})

	// Recalculate gizmo position when layer data changes (e.g. padding/margin edits)
	useEffect(() => {
		if (referenceEl) { update() }
	}, [layer, update, referenceEl])

	if (!visible || !referenceEl) { return null }

	return (
		<FloatingPortal>
			<div
				ref={refs.setFloating}
				data-render-exclude
				style={{
					...floatingStyles,
					pointerEvents: 'none',
					outline: border,
					outlineOffset: 0,
					overflow: 'visible',
				}}
			>
				{showBoxModel && layer && (
					<div style={{ opacity: boxModelVisible ? 1 : 0, transition: 'opacity 300ms ease-out' }}>
						<BoxModelOverlay layer={layer} />
					</div>
				)}
				{showDragOverlay && layer && 'floating' in layer && layer.floating && referenceEl && (
					<>
						<DragOverlay layerId={layer.id} referenceEl={referenceEl} />
						<AnchorMarker anchor={layer.floatAnchor} />
					</>
				)}
				{showResizeHandles && layer && RESIZABLE_LAYER_TYPES.has(layer.type) && referenceEl && (
					<ResizeHandles layerId={layer.id} layerType={layer.type} referenceEl={referenceEl} />
				)}
			</div>
		</FloatingPortal>
	)
}
